"use client";

import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Tags, Headset } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const features = [{
  title: "Resmi Temsilcilik",
  description: "Butzbach GMBH'nın Türkiye'deki resmi temsilcisi olarak, Alman mühendislik kalitesini doğrudan projelerinize taşıyoruz.",
  icon: MessageCircle
}, {
  title: "%100 Orijinal Yedek Parça",
  description: "Kapı sistemlerinizin uzun ömürlü ve güvenli çalışması için sadece Butzbach onaylı orijinal yedek parçalar sağlıyoruz.",
  icon: Tags
}, {
  title: "7/24 Teknik Destek",
  description: "Uzman mühendis kadromuzla arıza, bakım ve onarım ihtiyaçlarınızda günün her saati yanınızdayız.",
  icon: Headset
}];
export default function WhyChooseUs() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="223817" id="neden-tür-tor-teknik" className="bg-background py-24 px-4 sm:px-6 lg:px-8 border-t border-border" dir="ltr">
      <div ref={sectionRef} className={`mx-auto max-w-7xl transition-all duration-1000 transform ${isSectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground mb-4 tracking-tight">
            Neden TÜR TOR TEKNİK?
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default">
            Endüstriyel kapı çözümlerinde Butzbach güvencesi ve uzman mühendislik kadromuzla yanınızdayız.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          return <div data-index={i} key={i} className="h-full">
                <Card className="h-full bg-card border-border shadow-md hover:-translate-y-2 hover:shadow-xl transition-all duration-300 group rounded-md">
                  <CardHeader className="pb-4 pt-8 px-8">
                    <div className="relative inline-flex mb-6 transition-transform duration-300 group-hover:scale-110">
                      {}
                      <div className="p-4 bg-primary/10 rounded-md text-primary">
                        <Icon className="w-8 h-8" strokeWidth={2} />
                      </div>
                      {}
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary rounded-md border-2 border-card shadow-sm"></div>
                    </div>
                    <CardTitle className="text-xl font-bold font-default text-foreground uppercase tracking-wide">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="px-8 pb-8">
                    <p className="text-muted-foreground leading-relaxed font-default">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}